import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ChevronRight, MessageSquare } from 'lucide-react';
import { getDebateList } from '../../services/api';
import '../FullAnalysisModal/FullAnalysisModal.css';
import '../Article/ArticlePage.css';
import './DebateListPage.css';

const formatDate = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d)) return isoStr;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

const DebateListPage = () => {
  const navigate = useNavigate();
  const [debates, setDebates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const data = await getDebateList();
        if (!aborted) setDebates(data.list || []);
      } catch (e) {
        console.error(e);
        if (!aborted) setError(true);
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedFilter(searchTerm.trim().toUpperCase());
    }, 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  const displayChip = searchTerm.trim().toUpperCase();

  const filtered = debouncedFilter
    ? debates.filter((d) => {
        const keyword = (d.keyword || '').toUpperCase();
        const title = (d.title || '').toUpperCase();
        return keyword.includes(debouncedFilter) || title.includes(debouncedFilter);
      })
    : debates;

  const clearSearch = () => {
    setSearchTerm('');
    setDebouncedFilter('');
  };

  const openDebate = (aiDebateNo) => {
    navigate(`/debate/${aiDebateNo}`);
  };

  return (
    <div className="article-page debate-list-page">
      <div className="far-header">
        <div className="far-header-left">
          <span className="far-header-report-type">INVESTMENT DEBATE</span>
          <div className="far-header-title-row">
            <span className="far-header-title">AI 거장 토론</span>
          </div>
        </div>
      </div>

      {/* 거장 토론 소개 카드 */}
      <div className="debate-intro">
        <div className="debate-intro-card">
          <div className="debate-intro-eyebrow">매일 1종목 · 8명의 거장 · 2 라운드</div>
          <h2 className="debate-intro-headline">
            <span className="debate-intro-accent">오마하의 현인</span>이 패스한 종목을
            <br />
            <span className="debate-intro-accent">거품 사냥꾼</span>은 왜 숏 칠까요?
          </h2>
          <p className="debate-intro-body">
            매일 아침, 8명의 거장이 한 종목을 두고 격돌합니다.
            가치 투자부터 공매도, 파괴적 혁신, 사이클 거장까지 —
            오프닝과 반박, 입장 변화까지 8개의 시선을 한 화면에서.
          </p>
          <div className="debate-intro-masters">
            {[
              { label: '현인', tone: '#b42318' },
              { label: '여왕', tone: '#0f766e' },
              { label: '사냥꾼', tone: '#b42318' },
              { label: '원칙주의자', tone: '#5b4ab5' },
              { label: '퍼마불', tone: '#0f766e' },
              { label: '사도', tone: '#b42318' },
              { label: '항해사', tone: '#b45309' },
              { label: '시인', tone: '#b42318' },
            ].map((m) => (
              <span
                key={m.label}
                className="debate-intro-master"
                style={{ '--master-tone': m.tone }}
              >
                {m.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="article-search-wrap">
        <div className="article-search-bar">
          <Search size={16} className="article-search-icon" />
          <input
            type="text"
            className="article-search-input"
            placeholder="티커 또는 키워드로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
          {displayChip && (
            <span className="article-search-chip">
              {displayChip}
              <button onClick={clearSearch} aria-label="필터 해제">
                <X size={11} />
              </button>
            </span>
          )}
        </div>
      </div>

      <div className="article-body">
        {loading ? (
          [1, 2, 3].map((i) => (
            <div key={i} className="rep-skeleton-card">
              <div className="rep-skeleton rep-skeleton--tag" />
              <div className="rep-skeleton rep-skeleton--title-1" />
              <div className="rep-skeleton rep-skeleton--title-2" />
              <div className="rep-skeleton rep-skeleton--summary" />
              <div className="rep-skeleton rep-skeleton--date" />
            </div>
          ))
        ) : filtered.length > 0 ? (
          filtered.map((d) => {
            const tickerLabel = (d.keyword || '').toUpperCase();
            return (
              <div
                key={d.aiDebateNo}
                className="rep-card rep-card--debate"
                onClick={() => openDebate(d.aiDebateNo)}
              >
                <div className="rep-card-body">
                  <div className="rep-card-tag debate-card-tag-row">
                    <MessageSquare size={11} strokeWidth={2.4} />
                    <span>AI 거장 토론</span>
                    {tickerLabel && <span className="debate-card-ticker">${tickerLabel}</span>}
                  </div>
                  <div className="rep-card-title">{d.title}</div>
                  {d.summary && (
                    <div className="rep-card-summary">{d.summary}</div>
                  )}
                  <div className="rep-card-meta">
                    <span className="rep-card-date">{formatDate(d.updatedAt)}</span>
                  </div>
                </div>
                <ChevronRight size={18} className="rep-card-arrow" />
              </div>
            );
          })
        ) : error ? (
          <div className="article-empty">
            <div className="article-empty-icon">⚠️</div>
            <h3 className="article-empty-title">토론을 불러오지 못했습니다</h3>
            <p className="article-empty-desc">네트워크 연결을 확인하고 다시 시도해주세요.</p>
          </div>
        ) : (
          <div className="article-empty">
            <div className="article-empty-icon">🗣️</div>
            <h3 className="article-empty-title">
              {displayChip ? `'${displayChip}' 검색 결과 없음` : '예정된 토론이 없습니다'}
            </h3>
            <p className="article-empty-desc">
              {displayChip
                ? '다른 키워드로 검색해보세요.'
                : '거장 8인의 새 토론이 곧 업로드됩니다.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DebateListPage;
